import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import {
  Badge,
  Button,
  ButtonGroup,
  Card,
  DatePicker,
  Input,
  Modal,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Popover,
  PopoverAction,
  PopoverContent,
  Spinner,
  Switch,
  toast,
} from 'keep-react'

import { Wrapper } from 'components'
import { ArrowLeft, Calendar, FloppyDisk, Trash } from 'phosphor-react'
import { RouteNamesAuth } from 'routes/enum'
import { format } from 'date-fns'
import { TrainingItem } from 'api/types/trainings'
import { trainingService } from 'api/services/trainings'

// form validation schema
const validationSchema = Yup.object().shape({
  name: Yup.string().required('Obrigatório'),
  description: Yup.string(),
  date: Yup.string(),
  hours: Yup.number().min(0).max(23),
  minutes: Yup.number().min(0).max(59),
  link: Yup.string(),
  conferenceId: Yup.string(),
  conferenceVideo: Yup.string(),
  teacher: Yup.string(),
  tutorial: Yup.boolean(),
  status: Yup.boolean(),
  duration: Yup.number(),
})

export const BackofficeTraining = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  //first load and is api being called
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isApiBeingCalled, setIsApiBeingCalled] = useState<boolean>(false)

  //loaded training data
  const [data, setData] = useState<TrainingItem | null>(null)

  // Modal Control
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)

  //get training details
  const getTraining = async () => {
    setIsLoading(true)
    try {
      const response = (await trainingService.getTraining({
        id: id as string,
      })) as TrainingItem
      setData(response as TrainingItem)
      formik.setFieldValue('id', response.id)
      formik.setFieldValue('description', response.description || '')
      formik.setFieldValue('name', response.name || '')
      formik.setFieldValue(
        'date',
        response.date ? format(new Date(response.date), 'yyyy-MM-dd') : '',
      )
      formik.setFieldValue(
        'hours',
        response.date ? new Date(response.date).getHours() : 0,
      )
      formik.setFieldValue(
        'minutes',
        response.date ? new Date(response.date).getMinutes() : 0,
      )
      formik.setFieldValue('link', response.link || '')
      formik.setFieldValue('conferenceId', response.conferenceId || '')
      formik.setFieldValue('conferenceVideo', response.conferenceVideo || '')
      formik.setFieldValue('teacher', response.teacher || '')
      formik.setFieldValue('tutorial', response.tutorial)
      formik.setFieldValue('status', response.status)
      formik.setFieldValue('duration', response.duration || 0)

      setIsLoading(false)
    } catch (error) {
      toast.error('Não foi possível carregar a formação.')
    }
  }

  // using formik to handle form state
  const formik = useFormik({
    initialValues: {
      name: '',
      description: '',
      date: '',
      hours: 0,
      minutes: 0,
      link: '',
      conferenceId: '',
      conferenceVideo: '',
      teacher: '',
      tutorial: false,
      status: false,
      duration: 0,
    },
    validateOnBlur: false,
    validateOnChange: false,
    validationSchema,
    onSubmit: async values => {
      if (!id) return
      const newValues = {
        id: Number(id),
        name: values.name,
        description: values.description,
        date: '',
        link: values.link,
        conferenceId: values.conferenceId,
        conferenceVideo: values.conferenceVideo,
        teacher: values.teacher,
        tutorial: values.tutorial,
        status: values.status,
        duration: values.duration,
      }

      if (values.date) {
        const baseDate = new Date(values.date)
        const hours = Number(values.hours) || 0
        const minutes = Number(values.minutes) || 0

        const date = new Date(
          baseDate.getFullYear(),
          baseDate.getMonth(),
          baseDate.getDate(),
          hours,
          minutes,
          0,
          0,
        )

        newValues.date = format(date, "yyyy-MM-dd'T'HH:mm:ss")
      }

      try {
        setIsApiBeingCalled(true)
        await trainingService.saveTraining({
          trainingData: newValues,
        })

        toast.success('Formação atualizada com sucesso.')
      } catch (error) {
        toast.error('Erro ao atualizar formação.')
      } finally {
        setIsApiBeingCalled(false)
      }
    },
  })

  // delete training
  const deleteTraining = async () => {
    if (!data) return
    try {
      await trainingService.deleteTraining({
        id: String(data.id),
      })
      setShowDeleteModal(false)
      toast.success('Formação apagada com sucesso.')
      navigate(RouteNamesAuth.BACKOFFICE_TRAININGS)
    } catch (error) {
      toast.error('Não foi possível apagar a formação.')
    }
  }

  // initial data fetch
  useEffect(() => {
    getTraining()
  }, [id])

  return (
    <div className="w-full flex-1 py-12 overflow-auto">
      <Wrapper>
        {isLoading || !data ? (
          <Card className="w-full max-w-full p-4 md:p-6">
            <Spinner color="primary" className="mx-auto" />
          </Card>
        ) : (
          <>
            <Button
              variant="outline"
              color="secondary"
              className="mb-6"
              onClick={() => navigate(RouteNamesAuth.BACKOFFICE_TRAININGS)}
            >
              <ArrowLeft size={16} /> Formações
            </Button>
            <Card className="w-full max-w-full p-4 md:p-6">
              <h1 className="pb-10 text-xxxl font-medium text-metal-900 flex items-center justify-between">
                <span></span>
                <>
                  <ButtonGroup>
                    <Button
                      type="submit"
                      position="start"
                      color="secondary"
                      variant="default"
                      disabled={isApiBeingCalled}
                      className="gap-2"
                      onClick={() => {
                        formik.handleSubmit()
                      }}
                    >
                      <FloppyDisk size={16} />
                      Gravar alterações
                    </Button>

                    <Button
                      position="end"
                      variant="default"
                      color="error"
                      onClick={() => {
                        setShowDeleteModal(true)
                      }}
                      disabled={isApiBeingCalled}
                      className="gap-2"
                    >
                      <Trash size={16} />
                      Apagar Formação
                    </Button>
                  </ButtonGroup>
                </>
              </h1>

              <div className="space-y-8">
                <div className="grid grid-cols-[1fr_4fr] items-center gap-4">
                  <h3 className="text-body-4 font-bold text-metal-900">Nome</h3>
                  <Input
                    name="name"
                    value={formik.values.name ?? ''}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </div>

                <div className="grid grid-cols-[1fr_4fr] items-center gap-4">
                  <h3 className="text-body-4 font-bold text-metal-900">
                    Destaque
                  </h3>
                  <Input
                    name="description"
                    value={formik.values.description ?? ''}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </div>

                <div className="grid grid-cols-[1fr_4fr] items-center gap-4">
                  <h3 className="text-body-4 font-bold text-metal-900">
                    Formador
                  </h3>
                  <Input
                    name="teacher"
                    value={formik.values.teacher ?? ''}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </div>

                <div className="grid grid-cols-[1fr_4fr] items-center gap-4">
                  <h3 className="text-body-4 font-bold text-metal-900">Data</h3>
                  <div>
                    <Popover>
                      <PopoverAction asChild>
                        <Button
                          color="secondary"
                          size="lg"
                          className="w-[286px] justify-start gap-2 border border-metal-100 text-body-4 text-metal-900"
                          variant="outline"
                        >
                          <Calendar
                            size={20}
                            className="text-metal-400 dark:text-white"
                          />
                          {formik.values.date ? (
                            format(new Date(formik.values.date), 'dd/MM/yyyy')
                          ) : (
                            <span>Selecione a data da formação</span>
                          )}
                        </Button>
                      </PopoverAction>
                      <PopoverContent
                        align="start"
                        className="max-w-min border-0"
                      >
                        <DatePicker
                          dayShape="circle"
                          mode="single"
                          selected={
                            formik.values.date
                              ? new Date(formik.values.date)
                              : undefined
                          }
                          onSelect={value =>
                            formik.setFieldValue(
                              'date',
                              value ? format(value, 'yyyy-MM-dd') : '',
                            )
                          }
                          showOutsideDays={true}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                <div className="grid grid-cols-[1fr_4fr] items-center gap-4">
                  <h3 className="text-body-4 font-bold text-metal-900">
                    Horas
                  </h3>
                  <div className="flex itesm-center gap-4">
                    <Input
                      name="hours"
                      value={formik.values.hours ?? ''}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      placeholder="HH"
                      className="w-16 text-right"
                    />
                    <Input
                      name="minutes"
                      value={formik.values.minutes ?? ''}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      placeholder="MM"
                      className="w-16 text-right"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-[1fr_4fr] items-center gap-4">
                  <h3 className="text-body-4 font-bold text-metal-900">
                    Link para a conferência
                  </h3>
                  <Input
                    name="link"
                    value={formik.values.link ?? ''}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </div>

                <div className="grid grid-cols-[1fr_4fr] items-center gap-4">
                  <h3 className="text-body-4 font-bold text-metal-900">
                    ID da Conferência
                  </h3>
                  <Input
                    name="conferenceId"
                    value={formik.values.conferenceId ?? ''}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </div>

                <div className="grid grid-cols-[1fr_4fr] items-center gap-4">
                  <h3 className="text-body-4 font-bold text-metal-900">
                    ID do video da Conferência
                  </h3>
                  <Input
                    name="conferenceVideo"
                    value={formik.values.conferenceVideo ?? ''}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </div>

                <div className="grid grid-cols-[1fr_4fr] items-center gap-4">
                  <h3 className="text-body-4 font-bold text-metal-900">
                    Status
                  </h3>
                  <span className="flex items-center gap-2">
                    {data.status ? (
                      <Badge
                        className="pointer-events-none"
                        color="success"
                        variant="base"
                      >
                        Ativo
                      </Badge>
                    ) : (
                      <Badge
                        className="pointer-events-none"
                        color="error"
                        variant="base"
                      >
                        Inativo
                      </Badge>
                    )}
                    <Switch
                      checked={formik.values.status}
                      onCheckedChange={() => {
                        formik.setFieldValue('status', !formik.values.status)
                      }}
                      className="data-[state=checked]:bg-metal-500 text-metal-500 [&_span]:data-[state=checked]:before:bg-metal-500"
                    />
                  </span>
                </div>

                <div className="grid grid-cols-[1fr_4fr] items-center gap-4">
                  <h3 className="text-body-4 font-bold text-metal-900">
                    Tutorial?
                  </h3>
                  <span className="flex items-center gap-2">
                    {data.tutorial ? (
                      <Badge
                        className="pointer-events-none"
                        color="success"
                        variant="base"
                      >
                        Sim
                      </Badge>
                    ) : (
                      <Badge
                        className="pointer-events-none"
                        color="error"
                        variant="base"
                      >
                        Não
                      </Badge>
                    )}
                    <Switch
                      checked={formik.values.tutorial}
                      onCheckedChange={() => {
                        formik.setFieldValue(
                          'tutorial',
                          !formik.values.tutorial,
                        )
                      }}
                      className="data-[state=checked]:bg-metal-500 text-metal-500 [&_span]:data-[state=checked]:before:bg-metal-500"
                    />
                  </span>
                </div>
              </div>
            </Card>
          </>
        )}
      </Wrapper>

      {/* Delete Campaign Modal */}
      <Modal open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <ModalContent>
          <ModalHeader className="mb-6 space-y-3">
            <ModalTitle>Apagar Produto</ModalTitle>
            <ModalDescription>
              Quer apagar este produto?
              <br />
              <br />
              <strong className="text-error-500">
                Todos os dados deste produto irão ser perdidos.
              </strong>
            </ModalDescription>
          </ModalHeader>
          <ModalFooter className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              color="secondary"
              onClick={() => setShowDeleteModal(false)}
            >
              Cancel
            </Button>
            <Button type="submit" onClick={() => deleteTraining()}>
              Apagar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  )
}
